import { useCallback, useMemo } from 'react';

// Also cleared in DashboardLayout when leaving /calendar and lead overview.
const STORAGE_KEY = 'calendar-snapshot';

const VALID_VIEWS = new Set(['month', 'week', 'day']);
const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const CALENDAR_SNAPSHOT_DEFAULTS = {
  view: 'month',
  anchorKey: '',
  statusFilter: '',
  searchQuery: '',
  debouncedSearchQuery: '',
  gridScrollTop: 0,
  gridScrollLeft: 0,
  dashScrollTop: 0,
};

function sanitizeSnapshot(parsed) {
  const next = { ...CALENDAR_SNAPSHOT_DEFAULTS, ...(parsed || {}) };
  if (!VALID_VIEWS.has(next.view)) next.view = 'month';
  if (typeof next.anchorKey !== 'string' || !DAY_KEY_RE.test(next.anchorKey)) {
    next.anchorKey = '';
  }
  if (typeof next.statusFilter !== 'string') next.statusFilter = '';
  if (typeof next.searchQuery !== 'string') next.searchQuery = '';
  if (typeof next.debouncedSearchQuery !== 'string') {
    next.debouncedSearchQuery = next.searchQuery;
  }
  next.gridScrollTop = Number(next.gridScrollTop) || 0;
  next.gridScrollLeft = Number(next.gridScrollLeft) || 0;
  next.dashScrollTop = Number(next.dashScrollTop) || 0;
  return next;
}

function readSnapshot() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return sanitizeSnapshot(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeSnapshot(next) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function clearCalendarPersistence() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function useCalendarStateSnapshot() {
  const initialState = useMemo(
    () => readSnapshot() || { ...CALENDAR_SNAPSHOT_DEFAULTS },
    [],
  );

  const saveState = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    writeSnapshot(
      sanitizeSnapshot({
        ...(readSnapshot() || CALENDAR_SNAPSHOT_DEFAULTS),
        ...partial,
      }),
    );
  }, []);

  return {
    initialState,
    saveState,
  };
}
